import { supabase } from '../lib/supabase';

// UUID validation helper
const isValidUUID = (uuid) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex?.test(uuid);
};

export const subscriptionService = {
  // Get business subscription details
  async getBusinessSubscription(businessId) {
    try {
      // Validate businessId is a proper UUID
      if (!businessId || !isValidUUID(businessId)) {
        console.warn('Invalid business ID provided to getBusinessSubscription:', businessId);
        // Return default subscription state for invalid IDs
        return {
          hasSubscription: false,
          tier: 'basic',
          status: 'pending',
          servicesLimit: 0,
          servicesUsed: 0,
          isActive: false,
          isPremium: false,
          canAddServices: false
        };
      }

      const { data, error } = await supabase?.rpc('get_business_subscription_status', { business_uuid: businessId });

      if (error) throw error;

      const subscription = data?.[0];
      return {
        hasSubscription: subscription?.has_subscription || false,
        tier: subscription?.subscription_tier || 'basic',
        status: subscription?.subscription_status || 'pending',
        servicesLimit: subscription?.services_limit || 0,
        servicesUsed: subscription?.services_used || 0,
        isActive: subscription?.is_active || false,
        isPremium: subscription?.subscription_tier === 'premium' || subscription?.subscription_tier === 'premium_plus',
        canAddServices: subscription?.is_active && (subscription?.services_used < subscription?.services_limit)
      };
    } catch (error) {
      console.error('Error fetching business subscription:', error);
      // Return default subscription state on error
      return {
        hasSubscription: false,
        tier: 'basic',
        status: 'pending',
        servicesLimit: 0,
        servicesUsed: 0,
        isActive: false,
        isPremium: false,
        canAddServices: false
      };
    }
  },

  // Get available pricing tiers
  async getPricingTiers() {
    try {
      const { data, error } = await supabase?.from('service_tier_pricing')?.select('*')?.eq('is_active', true)?.order('annual_price', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching pricing tiers:', error);
      throw error;
    }
  },

  // Upgrade business subscription
  async upgradeSubscription(businessId, tierType, paymentReference = null) {
    try {
      // Validate businessId is a proper UUID
      if (!businessId || !isValidUUID(businessId)) {
        throw new Error('Invalid business ID provided for subscription upgrade');
      }

      // Get pricing for the tier
      const { data: pricing, error: pricingError } = await supabase?.from('service_tier_pricing')?.select('*')?.eq('tier_type', tierType)?.eq('is_active', true)?.single();

      if (pricingError) throw pricingError;

      // Update or create subscription
      const subscriptionData = {
        subscription_tier: tierType,
        subscription_status: 'active',
        services_limit: pricing?.services_included,
        annual_fee: pricing?.annual_price,
        subscription_start_date: new Date()?.toISOString(),
        subscription_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)?.toISOString(), // 1 year from now
        auto_renew: true,
        payment_reference: paymentReference,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('business_subscriptions')?.upsert(
          {
            business_id: businessId,
            owner_id: (await supabase?.auth?.getUser())?.data?.user?.id,
            ...subscriptionData
          },
          { onConflict: 'business_id' }
        )?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      throw error;
    }
  },

  // Check if user can add service to business
  async canAddService(businessId) {
    try {
      // Validate businessId is a proper UUID
      if (!businessId || !isValidUUID(businessId)) {
        console.warn('Invalid business ID provided to canAddService:', businessId);
        return false;
      }

      const { data, error } = await supabase?.rpc('can_add_service_to_business', { business_uuid: businessId });

      if (error) throw error;
      return data || false;
    } catch (error) {
      console.error('Error checking service limits:', error);
      return false;
    }
  },

  // Get user's businesses with subscription details
  async getUserBusinessesWithSubscriptions() {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('businesses')?.select(`
          *,
          business_subscriptions (
            subscription_tier,
            subscription_status,
            services_limit,
            services_used,
            subscription_end_date,
            annual_fee
          )
        `)?.eq('owner_id', user?.user?.id)?.eq('status', 'active');

      if (error) throw error;

      return data?.map(business => ({
        ...business,
        subscription: business?.business_subscriptions?.[0] || {
          subscription_tier: 'basic',
          subscription_status: 'pending',
          services_limit: 0,
          services_used: 0,
          annual_fee: 0.00
        }
      })) || [];
    } catch (error) {
      console.error('Error fetching user businesses with subscriptions:', error);
      throw error;
    }
  },

  // Cancel subscription
  async cancelSubscription(businessId) {
    try {
      // Validate businessId is a proper UUID
      if (!businessId || !isValidUUID(businessId)) {
        throw new Error('Invalid business ID provided for subscription cancellation');
      }

      const { data, error } = await supabase?.from('business_subscriptions')?.update({
          subscription_status: 'cancelled',
          auto_renew: false,
          updated_at: new Date()?.toISOString()
        })?.eq('business_id', businessId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }
};

export default subscriptionService;